import Conf from './Conf'
import Log from './Log'
import { CrossStorageClient } from 'cross-storage'

let localStorageAvailable = false
let sessionStorageAvailable = false
let crossStorageAvailable = null
let hubUrl = null
let crossStorageClient = null
let prefix = 'noci_'

export default class Storage {
  static init() {
    hubUrl = Conf.get().services.hub
    localStorageAvailable = this.isStorageAvailable('localStorage')
    sessionStorageAvailable = this.isStorageAvailable('sessionStorage')
  }

  static setPrefix(newPrefix) {
    prefix = newPrefix
  }

  static isStorageAvailable(type) {
    let storage = null

    try {
      storage = window[type]
      const x = '__storage_test__'
      storage.setItem(x, x)
      storage.removeItem(x)
      return true
    } catch (e) {
      return (
        e instanceof DOMException &&
        // everything except Firefox
        (e.code === 22 ||
          // Firefox
          e.code === 1014 ||
          // test name field too, because code might not be present
          // everything except Firefox
          e.name === 'QuotaExceededError' ||
          // Firefox
          e.name === 'NS_ERROR_DOM_QUOTA_REACHED') &&
        // acknowledge QuotaExceededError only if there's something already stored
        storage &&
        storage.length !== 0
      )
    }
  }

  static async crossStorageIsSupported() {
    try {
      await this.ensureCrossStorage()
      await crossStorageClient.onConnect()
      await crossStorageClient.get('test')
      return true
    } catch { }
    return false
  }

  static ensureCrossStorage() {
    return new Promise((resolve, reject) => {
      if (crossStorageClient !== null) {
        resolve()
        return
      }

      const poll = setInterval(() => {
        if (!window.document.body) return
        clearInterval(poll)
        crossStorageClient = new CrossStorageClient(hubUrl, {
          frameId: 'noci-cross-storage'
        })
        resolve()
      }, 100)
    })
  }

  /**
   * Gets a value from the storage
   *
   * @param key             Key
   * @param defaultValue    Default value to return if the key is not found in the storage
   *
   * @returns any
   */
  static async get(
    key,
    defaultValue = null,
    useSession = false
  ) {
    try {
      if (useSession) {
        if (sessionStorageAvailable) {
          return sessionStorage.getItem(prefix + key) || defaultValue
        } else {
          return this.getCookie(prefix + key) || defaultValue
        }
      } else {
        if (localStorageAvailable) {
          if (crossStorageAvailable === null) {
            crossStorageAvailable = await this.crossStorageIsSupported()
          }
          if (crossStorageAvailable) {
            await this.ensureCrossStorage()
            await crossStorageClient.onConnect()
            return (await crossStorageClient.get(prefix + key)) || defaultValue
          }
          return localStorage.getItem(prefix + key) || defaultValue
        } else {
          return this.getCookie(prefix + key) || defaultValue
        }
      }
    } catch (e) {
      Log.error(e)
    }
  }

  /**
   * Puts a value in the storage
   *
   * @param key     Key
   * @param value   Value
   */
  static async set(key, value, useSession = false) {
    try {
      if (useSession) {
        if (sessionStorageAvailable) {
          return sessionStorage.setItem(prefix + key, value)
        } else {
          return this.setCookie(prefix + key, value, 1 / 48) // Keep it 30 minutes
        }
      } else {
        if (localStorageAvailable) {
          if (crossStorageAvailable === null) {
            crossStorageAvailable = await this.crossStorageIsSupported()
          }
          if (crossStorageAvailable) {
            await this.ensureCrossStorage()
            await crossStorageClient.onConnect()
            return await crossStorageClient.set(prefix + key, value)
          }
          return localStorage.setItem(prefix + key, value)
        } else {
          return this.setCookie(prefix + key, value)
        }
      }
    } catch (e) {
      Log.error(e)
    }
  }

  /**
   * Deletes a value in the storage
   *
   * @param key     Key
   */
  static async delete(key) {
    try {
      if (localStorageAvailable) {
        if (crossStorageAvailable === null) {
          crossStorageAvailable = await this.crossStorageIsSupported()
        }
        if (crossStorageAvailable) {
          await this.ensureCrossStorage()
          await crossStorageClient.onConnect()
          return await crossStorageClient.del(prefix + key)
        }
        return localStorage.removeItem(prefix + key)
      } else {
        return this.deleteCookie(prefix + key)
      }
    } catch (e) {
      Log.error(e)
    }
  }

  /**
   * Sets a cookie with a value
   *
   * @param name                Key
   * @param val                 Value
   * @param expiresInDays       Sets the expiration date in X days
   */
  static setCookie(name, val, expiresInDays = 30) {
    try {
      const date = new Date()
      const value = val

      date.setTime(date.getTime() + expiresInDays * 24 * 60 * 60 * 1000)

      document.cookie =
        name + '=' + value + '; expires=' + date.toUTCString() + '; path=/'
    } catch (e) {
      Log.error(e)
    }
  }

  /**
   * Gets the value of a cookie
   *
   * @param name    Key
   */
  static getCookie(name) {
    try {
      const value = '; ' + document.cookie
      const parts = value.split('; ' + name + '=')

      if (parts.length === 2) {
        return parts
          .pop()
          .split(';')
          .shift()
      }
      return null
    } catch (e) {
      Log.error(e)
    }
  }

  /**
   * Deletes a cookie
   *
   * @param name    Key
   */
  static deleteCookie(name) {
    try {
      const date = new Date()

      // Set it expire in -1 days
      date.setTime(date.getTime() + -1 * 24 * 60 * 60 * 1000)

      // Set it
      document.cookie = name + '=; expires=' + date.toUTCString() + '; path=/'
    } catch (e) {
      Log.error(e)
    }
  }
}
